import React, { useMemo, useRef } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  Wallet,
  Users2,
  Layers,
  TrendingUp,
  TrendingDown,
  Receipt,
  ArrowDownLeft,
  ArrowUpRight,
  Landmark,
  Building2,
  Percent,
  ArrowLeft,
} from 'lucide-react';
import { useAdminEmiPayments, useUnifiedCustomers } from '../lib/workspaceStore';
import { useConsultancyBundle } from '../lib/consultancyStore';
import { useAccountingBundle } from '../lib/accountingStore';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { useDateRange, filterByRange, formatRangeLabel, inRange, toDate } from '../lib/dateFilter';
import { ExportMenu, type ExportRow } from '../lib/exportReports';
import { DashboardCard } from './dashboard/DashboardCard';
import { useTranslation } from '../lib/i18n';
import { useChartTheme } from '../lib/chartTheme';
import { computePaymentFlows, computeLiveCashBank } from '../lib/paymentFlows';
import {
  computeFinancePeriodMetrics,
  computePortfolioTotals,
  interestRevenueFromPayment,
  lateFeeCashFromPayment,
  emiCashFromPayment,
} from '../lib/portfolioMetrics';
import { resolveCashBankAmounts } from '../lib/paymentVia';
import { useBooksOpening } from '../lib/booksOpeningStore';

const inr = (n: number) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
};

/** Mid-year YTD seeds apply when the selected window starts on/before cutover. */
function includeBooksYtd(range: { start: Date | null; end: Date | null }, cutoverYmd: string): boolean {
  if (!cutoverYmd) return false;
  const cut = toDate(cutoverYmd);
  if (!cut) return false;
  if (range.start && range.start.getTime() > cut.getTime() + 86400000 - 1) return false;
  return true;
}

interface AccountingDashboardProps {
  setActiveItem?: (id: string) => void;
  onBack?: () => void;
}

export function AccountingDashboard({ setActiveItem, onBack }: AccountingDashboardProps) {
  const { t } = useTranslation();
  const chart = useChartTheme();
  const payments = useAdminEmiPayments();
  const customers = useUnifiedCustomers();
  const [consult] = useConsultancyBundle();
  const [acct] = useAccountingBundle();
  const [books] = useBooksOpening();
  const dr = useDateRange('financial_year');

  const overviewRef = useRef<HTMLElement | null>(null);
  const financeRef = useRef<HTMLElement | null>(null);
  const consultancyRef = useRef<HTMLElement | null>(null);

  const scrollTo = (el: HTMLElement | null) => {
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const applyYtd = includeBooksYtd(dr.range, books.cutoverDate);
  const ytdInterest = applyYtd ? Math.max(0, Number(books.balances.ytdInterestIncome) || 0) : 0;
  const ytdExpenses = applyYtd ? Math.max(0, Number(books.balances.ytdExpenses) || 0) : 0;

  /* ---- Finance: revenue = interest + late fees + opening YTD seed ---- */
  const fin = useMemo(() => {
    const metrics = computeFinancePeriodMetrics(payments, dr.range, ytdInterest);
    const portfolio = computePortfolioTotals(customers);
    const inWin = filterByRange(payments, dr.range, (p) => p.paidAt);

    let cashCollections = 0;
    let bankCollections = 0;
    for (const p of inWin) {
      const total =
        (p.amount || 0) +
        (Number(p.lateFeeCollected) > 0 ? Number(p.lateFeeCollected) : p.lateFeeAmount || 0);
      const split = resolveCashBankAmounts(p.paymentVia, total, p.cashAmount, p.bankAmount);
      cashCollections += split.cash;
      bankCollections += split.bank;
    }

    const byMonth = new Map<string, { collected: number; interest: number; late: number }>();
    for (const p of inWin) {
      const d = new Date(p.paidAt);
      if (Number.isNaN(d.getTime())) continue;
      const k = monthKey(d);
      const cur = byMonth.get(k) || { collected: 0, interest: 0, late: 0 };
      cur.collected += emiCashFromPayment(p);
      cur.interest += interestRevenueFromPayment(p);
      cur.late += lateFeeCashFromPayment(p);
      byMonth.set(k, cur);
    }
    const trend = [...byMonth.entries()].sort().map(([k, v]) => ({
      name: monthLabel(k),
      collected: Math.round(v.collected),
      interest: Math.round(v.interest),
      late: Math.round(v.late),
    }));

    return {
      emiCollected: metrics.emiCollected,
      lateFees: metrics.lateFeesCollected,
      lateFeesCollected: metrics.lateFeesCollected,
      interestCollected: metrics.interestCollected,
      ytdInterest,
      principalCollected: metrics.principalCollected,
      totalCollections: metrics.totalCollections,
      cashCollections,
      bankCollections,
      principal: portfolio.principal,
      outstanding: portfolio.outstanding,
      borrowers: portfolio.borrowers,
      revenue: metrics.revenue,
      trend,
    };
  }, [payments, customers, dr.range, ytdInterest]);

  const flows = useMemo(() => computePaymentFlows(dr.range, payments), [dr.range, payments]);
  const liveCashBank = useMemo(() => computeLiveCashBank(books, payments), [books, payments]);

  /* ---- Consultancy ---- */
  const con = useMemo(() => {
    const sales = (consult.sales || []).filter((s) => inRange(s.recordedAt, dr.range));
    let revenue = 0;
    let cogs = 0;
    const byMonth = new Map<string, { revenue: number; profit: number }>();
    for (const s of sales) {
      const price = Math.max(0, parseFloat(String(s.sellingPrice ?? '')) || 0);
      const cost = Math.max(0, Number(s.acquisitionCostAtSale) || 0);
      revenue += price;
      cogs += cost;
      const d = new Date(String(s.recordedAt));
      if (Number.isNaN(d.getTime())) continue;
      const k = monthKey(d);
      const cur = byMonth.get(k) || { revenue: 0, profit: 0 };
      cur.revenue += price;
      cur.profit += price - cost;
      byMonth.set(k, cur);
    }
    const stockInvestment = (consult.vehicles || [])
      .filter((v) => v.status === 'available')
      .reduce((s, v) => s + (Number(v.price) || 0), 0);
    const trend = [...byMonth.entries()].sort().map(([k, v]) => ({
      name: monthLabel(k),
      revenue: Math.round(v.revenue),
      profit: Math.round(v.profit),
    }));
    return { revenue, cogs, profit: revenue - cogs, unitsSold: sales.length, stockInvestment, trend };
  }, [consult, dr.range]);

  /* ---- Expenses ---- */
  const exp = useMemo(() => {
    const inWin = filterByRange(acct.expenses, dr.range, (e) => e.expenseDate);
    const periodTotal = inWin.reduce((s, e) => s + (e.amount || 0), 0);
    const total = periodTotal + ytdExpenses;
    const byCat = new Map<string, number>();
    const byMonth = new Map<string, number>();
    for (const e of inWin) {
      byCat.set(e.category, (byCat.get(e.category) || 0) + (e.amount || 0));
      const d = new Date(e.expenseDate);
      if (!Number.isNaN(d.getTime())) byMonth.set(monthKey(d), (byMonth.get(monthKey(d)) || 0) + (e.amount || 0));
    }
    if (ytdExpenses > 0) {
      byCat.set('YTD (opening)', (byCat.get('YTD (opening)') || 0) + ytdExpenses);
    }
    const pie = [...byCat.entries()].sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value: Math.round(value) }));
    return { total, periodTotal, ytdExpenses, pie, byMonth };
  }, [acct.expenses, dr.range, ytdExpenses]);

  /* Finance profit = revenue (interest + late fees) − expenses */
  const financeProfit = fin.revenue - exp.total;

  /* Combined P&L */
  const combined = useMemo(() => {
    const revenue = fin.revenue + con.profit;
    const net = revenue - exp.total;
    const interestByMonth = new Map<string, number>();
    const lateByMonth = new Map<string, number>();
    for (const p of filterByRange(payments, dr.range, (x) => x.paidAt)) {
      const d = new Date(p.paidAt);
      if (Number.isNaN(d.getTime())) continue;
      const k = monthKey(d);
      interestByMonth.set(k, (interestByMonth.get(k) || 0) + interestRevenueFromPayment(p));
      lateByMonth.set(k, (lateByMonth.get(k) || 0) + lateFeeCashFromPayment(p));
    }
    const consultProfitByMonth = new Map<string, number>();
    for (const s of (consult.sales || []).filter((x) => inRange(x.recordedAt, dr.range))) {
      const d = new Date(String(s.recordedAt));
      if (Number.isNaN(d.getTime())) continue;
      const profit = (parseFloat(String(s.sellingPrice ?? '')) || 0) - (Number(s.acquisitionCostAtSale) || 0);
      consultProfitByMonth.set(monthKey(d), (consultProfitByMonth.get(monthKey(d)) || 0) + profit);
    }
    const keys = new Set<string>([
      ...interestByMonth.keys(),
      ...lateByMonth.keys(),
      ...consultProfitByMonth.keys(),
      ...exp.byMonth.keys(),
    ]);
    const trend = [...keys].sort().map((k) => {
      const rev =
        (interestByMonth.get(k) || 0) + (lateByMonth.get(k) || 0) + (consultProfitByMonth.get(k) || 0);
      const expense = exp.byMonth.get(k) || 0;
      return {
        name: monthLabel(k),
        revenue: Math.round(rev),
        expense: Math.round(expense),
        profit: Math.round(rev - expense),
      };
    });
    return { revenue, net, financeProfit: fin.revenue - exp.total, trend };
  }, [fin.revenue, con.profit, exp.total, exp.byMonth, payments, consult.sales, dr.range]);

  const exportRows: ExportRow[] = [
    { Section: 'Finance', Metric: 'Total revenue (interest + late fees)', Amount: Math.round(fin.revenue) },
    { Section: 'Finance', Metric: 'Interest collected', Amount: Math.round(fin.interestCollected) },
    { Section: 'Finance', Metric: 'Late fees collected', Amount: Math.round(fin.lateFeesCollected) },
    { Section: 'Finance', Metric: 'Total EMI collected', Amount: Math.round(fin.emiCollected) },
    { Section: 'Finance', Metric: 'Total collections (EMI + late)', Amount: Math.round(fin.totalCollections) },
    { Section: 'Finance', Metric: 'Principal collected', Amount: Math.round(fin.principalCollected) },
    { Section: 'Finance', Metric: 'Finance profit (revenue − expenses)', Amount: Math.round(financeProfit) },
    { Section: 'Expenses', Metric: 'Total expenses', Amount: Math.round(exp.total) },
    { Section: 'Consultancy', Metric: 'Revenue', Amount: Math.round(con.revenue) },
    { Section: 'Consultancy', Metric: 'Cost of goods', Amount: Math.round(con.cogs) },
    { Section: 'Consultancy', Metric: 'Profit', Amount: Math.round(con.profit) },
    { Section: 'Combined', Metric: 'Net profit', Amount: Math.round(combined.net) },
    { Section: 'Cash & bank', Metric: 'Cash taken (collections)', Amount: flows.cashTaken },
    { Section: 'Cash & bank', Metric: 'Bank credit (collections)', Amount: flows.bankCredited },
    { Section: 'Cash & bank', Metric: 'Cash given (loan disbursed)', Amount: flows.cashGiven },
    { Section: 'Cash & bank', Metric: 'Bank debit (loan disbursed)', Amount: flows.bankDebited },
  ];

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header">
        <div>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mb-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('hub.backToModules')}
            </button>
          ) : null}
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">
            {t('hub.accountingSummary')}
          </p>
          <h1 className="app-page-title mt-0.5">{t('hub.accountingTitle')}</h1>
          <p className="app-page-sub">
            {t('common.finance')}, {t('nav.consultancy')} — {formatRangeLabel(dr.range)}
          </p>
        </div>
        <div className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <DateRangeFilter
            start={dr.range.start}
            end={dr.range.end}
            preset={dr.preset}
            onStart={dr.setStart}
            onEnd={dr.setEnd}
            onPreset={dr.setPreset}
            onClear={dr.clear}
            compact
          />
          <ExportMenu title={`Accounting Summary (${formatRangeLabel(dr.range)})`} excelFilename="accounting-summary.xls" rows={exportRows} />
        </div>
      </header>

      {/* Jump links — scroll only; never hide other sections */}
      <div className="grid w-full grid-cols-3 gap-1 rounded-lg border border-zinc-200 bg-white p-1 sm:inline-flex sm:w-auto">
        <button
          type="button"
          onClick={() => scrollTo(overviewRef.current)}
          className="rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-700 transition hover:bg-emerald-50 sm:px-4 sm:text-sm"
        >
          {t('master.tabCombined')}
        </button>
        <button
          type="button"
          onClick={() => scrollTo(financeRef.current)}
          className="rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-700 transition hover:bg-emerald-50 sm:px-4 sm:text-sm"
        >
          {t('master.tabFinance')}
        </button>
        <button
          type="button"
          onClick={() => scrollTo(consultancyRef.current)}
          className="rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-700 transition hover:bg-emerald-50 sm:px-4 sm:text-sm"
        >
          {t('master.tabConsultancy')}
        </button>
      </div>

      {/* ── Combined P&L overview (always visible) ── */}
      <section ref={overviewRef} className="scroll-mt-20 space-y-4">
          <h2 className="text-sm font-semibold text-zinc-900">{t('master.tabCombined')}</h2>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
          <DashboardCard
            title={t('common.outstanding')}
            value={inr(fin.outstanding)}
            hint={t('master.portfolioHint')}
            icon={TrendingDown}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => setActiveItem?.('customers')}
          />
          <DashboardCard
            title={t('common.principal')}
            value={inr(fin.principal)}
            hint={`${fin.borrowers} ${t('master.borrowersHint')}`}
            icon={Layers}
            accentTop="border-l-[3px] border-l-zinc-400"
            iconColor="text-zinc-600"
            onClick={() => setActiveItem?.('customers')}
          />
          <DashboardCard
            title={t('master.openingCash')}
            value={inr(liveCashBank.cash)}
            hint={t('master.liveCashHint')}
            icon={Wallet}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => setActiveItem?.('accounts')}
          />
          <DashboardCard
            title={t('master.openingBank')}
            value={inr(liveCashBank.bank)}
            hint={t('master.liveBankHint')}
            icon={Landmark}
            accentTop="border-l-[3px] border-l-sky-600"
            iconColor="text-sky-700"
            onClick={() => setActiveItem?.('accounts')}
          />
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
          <DashboardCard
            title={t('master.totalRevenue')}
            value={inr(fin.revenue)}
            hint={
              fin.ytdInterest > 0
                ? t('master.interestWithYtdHint')
                : t('master.revenueHint')
            }
            icon={Percent}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => scrollTo(financeRef.current)}
          />
          <DashboardCard
            title={t('master.expenses')}
            value={inr(exp.total)}
            change="—"
            icon={Receipt}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => setActiveItem?.('expenses')}
          />
          <DashboardCard
            title={t('master.financeProfit')}
            value={inr(financeProfit)}
            hint={t('master.profitFormula')}
            icon={TrendingUp}
            accentTop={`border-l-[3px] ${financeProfit >= 0 ? 'border-l-teal-600' : 'border-l-rose-600'}`}
            iconColor={financeProfit >= 0 ? 'text-teal-700' : 'text-rose-600'}
            onClick={() => scrollTo(financeRef.current)}
          />
          <DashboardCard
            title={t('master.consultancyProfit')}
            value={inr(con.profit)}
            change="—"
            icon={Users2}
            accentTop={`border-l-[3px] ${con.profit >= 0 ? 'border-l-sky-600' : 'border-l-rose-600'}`}
            iconColor={con.profit >= 0 ? 'text-sky-700' : 'text-rose-600'}
            onClick={() => scrollTo(consultancyRef.current)}
          />
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
          <DashboardCard
            title={t('master.netAccounting')}
            value={inr(combined.net)}
            hint={t('master.netFormula')}
            icon={Layers}
            accentTop={`border-l-[3px] ${combined.net >= 0 ? 'border-l-emerald-600' : 'border-l-rose-600'}`}
            iconColor={combined.net >= 0 ? 'text-emerald-700' : 'text-rose-600'}
            onClick={() => {}}
          />
          <DashboardCard
            title={t('master.emiCollected')}
            value={inr(fin.emiCollected)}
            change="—"
            icon={Wallet}
            accentTop="border-l-[3px] border-l-zinc-400"
            iconColor="text-zinc-600"
            onClick={() => scrollTo(financeRef.current)}
          />
        </div>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-zinc-900">{t('dashboard.cashBankFlows')}</h3>
          <p className="text-xs leading-snug text-zinc-500">{t('master.cashBankHint')}</p>
          <div className="grid grid-cols-2 gap-2 sm:gap-2.5 lg:grid-cols-4">
            <DashboardCard
              dense
              title={t('payment.cashTaken')}
              value={inr(flows.cashTaken)}
              hint={t('payment.collectionsIn')}
              icon={ArrowDownLeft}
              accentTop="border-l-[3px] border-l-emerald-600"
              iconColor="text-emerald-700"
              onClick={() => {}}
            />
            <DashboardCard
              dense
              title={t('payment.accountCredited')}
              value={inr(flows.bankCredited)}
              hint={t('payment.collectionsIn')}
              icon={Landmark}
              accentTop="border-l-[3px] border-l-sky-600"
              iconColor="text-sky-700"
              onClick={() => {}}
            />
            <DashboardCard
              dense
              title={t('payment.cashGiven')}
              value={inr(flows.cashGiven)}
              hint={t('payment.loanDisbursed')}
              icon={ArrowUpRight}
              accentTop="border-l-[3px] border-l-amber-500"
              iconColor="text-amber-700"
              onClick={() => {}}
            />
            <DashboardCard
              dense
              title={t('payment.accountDebited')}
              value={inr(flows.bankDebited)}
              hint={t('payment.loanDisbursed')}
              icon={Building2}
              accentTop="border-l-[3px] border-l-indigo-600"
              iconColor="text-indigo-700"
              onClick={() => {}}
            />
          </div>
        </section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <ChartCard title={t('master.incomeVsExpenses')} className="lg:col-span-2">
            {combined.trend.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={combined.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={chart.axis} />
                  <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} />
                  <Tooltip formatter={(v: number) => inr(v)} />
                  <Legend />
                  <Bar dataKey="revenue" fill={chart.barTrio[0]} name={t('master.totalRevenue')} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" fill={chart.barTrio[1]} name={t('master.expenses')} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="profit" fill={chart.barTrio[2]} name={t('master.financeProfit')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty label={t('dashboard.noData')} />
            )}
          </ChartCard>
          <ChartCard title={t('master.spendByCategory')}>
            {exp.pie.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={exp.pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={false}>
                    {exp.pie.map((_, i) => (
                      <Cell key={i} fill={chart.series[i % chart.series.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => inr(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty label={t('dashboard.noData')} />
            )}
          </ChartCard>
        </div>
      </section>

      {/* ── Finance section (always visible) ── */}
      <section ref={financeRef} className="scroll-mt-20 space-y-4 border-t border-zinc-200 pt-6">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">{t('master.tabFinance')}</h2>
          <p className="mt-0.5 text-xs text-zinc-500">{t('master.financeSectionHint')}</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
          <DashboardCard
            title={t('master.totalRevenue')}
            value={inr(fin.revenue)}
            hint={
              fin.ytdInterest > 0
                ? t('master.interestWithYtdHint')
                : t('master.revenueHint')
            }
            icon={Percent}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('master.expenses')}
            value={inr(exp.total)}
            change="—"
            icon={Receipt}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => setActiveItem?.('expenses')}
          />
          <DashboardCard
            title={t('master.financeProfit')}
            value={inr(financeProfit)}
            hint={t('master.profitFormula')}
            icon={TrendingUp}
            accentTop={`border-l-[3px] ${financeProfit >= 0 ? 'border-l-teal-600' : 'border-l-rose-600'}`}
            iconColor={financeProfit >= 0 ? 'text-teal-700' : 'text-rose-600'}
            onClick={() => {}}
          />
          <DashboardCard
            title={t('master.emiCollected')}
            value={inr(fin.emiCollected)}
            change="—"
            icon={Wallet}
            accentTop="border-l-[3px] border-l-zinc-400"
            iconColor="text-zinc-600"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('payment.cashTaken')}
            value={inr(flows.cashTaken)}
            hint={t('payment.collectionsIn')}
            icon={ArrowDownLeft}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('payment.accountCredited')}
            value={inr(flows.bankCredited)}
            hint={t('payment.collectionsIn')}
            icon={Landmark}
            accentTop="border-l-[3px] border-l-sky-600"
            iconColor="text-sky-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('payment.cashGiven')}
            value={inr(flows.cashGiven)}
            hint={t('payment.loanDisbursed')}
            icon={ArrowUpRight}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('payment.accountDebited')}
            value={inr(flows.bankDebited)}
            hint={t('payment.loanDisbursed')}
            icon={Building2}
            accentTop="border-l-[3px] border-l-indigo-600"
            iconColor="text-indigo-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('reports.lateFees')}
            value={inr(fin.lateFeesCollected)}
            change="—"
            icon={TrendingUp}
            accentTop="border-l-[3px] border-l-teal-600"
            iconColor="text-teal-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('common.principal')}
            value={inr(fin.principal)}
            change="—"
            icon={Layers}
            accentTop="border-l-[3px] border-l-zinc-400"
            iconColor="text-zinc-600"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('common.outstanding')}
            value={inr(fin.outstanding)}
            change="—"
            icon={TrendingDown}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => {}}
          />
        </div>
        <ChartCard title={t('master.monthlyCollections')}>
          {fin.trend.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={fin.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={chart.axis} />
                <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} />
                <Tooltip formatter={(v: number) => inr(v)} />
                <Legend />
                <Bar dataKey="collected" fill={chart.barTrio[0]} name={t('master.emiCollected')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="interest" fill={chart.barTrio[1]} name={t('master.interestCollectedHint')} radius={[4, 4, 0, 0]} />
                <Bar dataKey="late" fill={chart.barTrio[2]} name={t('reports.lateFees')} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Empty label={t('dashboard.noData')} />
          )}
        </ChartCard>
      </section>

      {/* ── Consultancy section (always visible) ── */}
      <section ref={consultancyRef} className="scroll-mt-20 space-y-4 border-t border-zinc-200 pt-6">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">{t('master.tabConsultancy')}</h2>
          <p className="mt-0.5 text-xs text-zinc-500">{t('master.consultancySectionHint')}</p>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
          <DashboardCard
            title={t('consultancy.revenue')}
            value={inr(con.revenue)}
            change="—"
            icon={Wallet}
            accentTop="border-l-[3px] border-l-emerald-600"
            iconColor="text-emerald-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('consultancy.investment')}
            value={inr(con.cogs)}
            change="—"
            icon={TrendingDown}
            accentTop="border-l-[3px] border-l-amber-500"
            iconColor="text-amber-700"
            onClick={() => {}}
          />
          <DashboardCard
            title={t('consultancy.profit')}
            value={inr(con.profit)}
            change="—"
            icon={TrendingUp}
            accentTop={`border-l-[3px] ${con.profit >= 0 ? 'border-l-teal-600' : 'border-l-rose-600'}`}
            iconColor={con.profit >= 0 ? 'text-teal-700' : 'text-rose-600'}
            onClick={() => {}}
          />
          <DashboardCard
            title={t('consultancy.sold')}
            value={String(con.unitsSold)}
            change="—"
            icon={Layers}
            accentTop="border-l-[3px] border-l-zinc-400"
            iconColor="text-zinc-600"
            onClick={() => {}}
          />
        </div>
        <ChartCard title={t('master.revenueVsProfit')}>
          {con.trend.length ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={con.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={chart.axis} />
                <YAxis tick={{ fontSize: 11 }} stroke={chart.axis} />
                <Tooltip formatter={(v: number) => inr(v)} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke={chart.secondary} name={t('consultancy.revenue')} strokeWidth={2} />
                <Line type="monotone" dataKey="profit" stroke={chart.primary} name={t('consultancy.profit')} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Empty label={t('dashboard.noData')} />
          )}
        </ChartCard>
      </section>

      <p className="mt-4 text-center text-xs text-zinc-400">
        <button type="button" className="app-btn-ghost" onClick={() => setActiveItem?.('expenses')}>
          {t('nav.expenses')}
        </button>{' '}
        ·{' '}
        <button type="button" className="app-btn-ghost" onClick={() => setActiveItem?.('employees')}>
          {t('nav.employees')}
        </button>
      </p>
    </div>
  );
}

function ChartCard({ title, className = '', children }: { title: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={`app-card app-chart-wrap app-card-inner ${className}`}>
      <h3 className="mb-3 text-sm font-semibold text-zinc-900">{title}</h3>
      {children}
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="flex h-[280px] items-center justify-center text-sm text-zinc-400">{label}</div>;
}

export default AccountingDashboard;
